import { showNewMessage } from '@intercom/messenger-js-sdk';
import { InputGroupAddon } from '@plato/ui/primitives/input-group';
import { CircleHelpIcon, ThumbsDownIcon, ThumbsUpIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { Badge } from '../../../components/ui/badge.tsx';
import { Button } from '../../../components/ui/button.tsx';
import Text from '../../../components/ui/text.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { AiChatSupportTrigger } from '../../analytics/types/analytics-events.ts';
import { AiChatIcon } from '../assets/ai-chat-icon.tsx';
import type { AskPlatoChat } from '../hooks/use-ask-plato-chat.ts';
import { AskPlatoEmptyState } from './ask-plato-empty-state.tsx';
import { MessageActionButton, MessageActions } from './message-actions.tsx';
import {
    MessageScroller,
    MessageScrollerButton,
    MessageScrollerContent,
    MessageScrollerItem,
    MessageScrollerProvider,
    MessageScrollerViewport,
} from './message-scroller.tsx';
import { Message, MessageContent } from './message.tsx';
import { PromptInput, PromptInputSubmit, PromptInputTextarea } from './prompt-input.tsx';
import {
    SidePanelClose,
    SidePanelContent,
    SidePanelFooter,
    SidePanelHeader,
    SidePanelTitle,
} from './side-panel.tsx';

export type AskPlatoPanelProps = {
    chat: AskPlatoChat;
    docked?: boolean;
};

export function AskPlatoPanel({ chat, docked }: AskPlatoPanelProps) {
    const { t } = useLocale('common');
    const sendEvent = useAnalytics();
    const [draft, setDraft] = useState('');
    const composerRef = useRef<HTMLTextAreaElement>(null);

    const { messages, isStreaming, hasError, activity, sendMessage, toggleRating } = chat;
    const lastMessage = messages.at(-1);
    const lastMessageId = lastMessage?.id;

    // A reply fails in two ways: the server classifies it `failure` and closes
    // the stream cleanly, or the stream throws and never reports at all. Both
    // are the same thing to the user.
    const replyFailed = hasError || lastMessage?.status === 'failure';
    // `unclear_request` is not a failure — the model answered without reaching
    // for data, typically asking something back. There is real text on screen,
    // so it gets the escalation without the error line above it.
    const replyUnclear = lastMessage?.status === 'unclear_request';
    // `hasError` wins: a thrown stream has no status the server stands behind.
    const supportTrigger: AiChatSupportTrigger = hasError
        ? 'error'
        : replyUnclear
          ? 'unclear_request'
          : 'failure';
    const lastQuestion = messages.findLast((message) => message.role === 'user')?.text;

    return (
        <SidePanelContent
            docked={docked}
            // Opening the panel means wanting to ask something, so focus the
            // composer rather than the close button the primitive would pick.
            // Touch keeps the default, where focusing a textarea would raise the
            // virtual keyboard over the panel.
            initialFocus={(openType) => (openType === 'touch' ? true : composerRef.current)}
        >
            <SidePanelHeader>
                <AiChatIcon gradient className="h-5 w-5 shrink-0" />
                <SidePanelTitle className="mr-0">{t('ask_plato__title')}</SidePanelTitle>
                {/* Matches the `Alpha` marker on Plato Revenue — same hardcoded,
                    untranslated wording for a product-maturity label. */}
                <Badge variant="blue" className="mr-auto text-xs">
                    Alpha
                </Badge>
                <SidePanelClose label={t('ask_plato__close')} />
            </SidePanelHeader>

            <MessageScrollerProvider>
                <MessageScroller className="min-h-0 flex-1">
                    <MessageScrollerViewport>
                        <MessageScrollerContent className="px-container py-container">
                            {messages.length === 0 && <AskPlatoEmptyState />}

                            {messages.map((message) => (
                                <MessageScrollerItem key={message.id} messageId={message.id}>
                                    {/* A finished reply with no text has nothing to
                                        say — a `failure` leaves the bubble empty, and
                                        the loading dots would sit there for good. */}
                                    {(message.text.length > 0 || isStreaming) && (
                                        <Message align={message.role === 'user' ? 'end' : 'start'}>
                                            <MessageContent
                                                variant={message.role === 'user' ? 'tinted' : 'ghost'}
                                            >
                                                {message.text.length === 0 ? t('loading_dots') : message.text}
                                            </MessageContent>
                                        </Message>
                                    )}

                                    {message.role === 'assistant' &&
                                        message.text.length > 0 &&
                                        !(isStreaming && message.id === lastMessageId) && (
                                            <MessageActions className="mt-tight -ml-1.5">
                                                <MessageActionButton
                                                    label={t('ask_plato__like')}
                                                    active={message.rating === 'like'}
                                                    onClick={() => {
                                                        toggleRating(message.id, 'like');
                                                    }}
                                                >
                                                    <ThumbsUpIcon />
                                                </MessageActionButton>
                                                <MessageActionButton
                                                    label={t('ask_plato__dislike')}
                                                    active={message.rating === 'dislike'}
                                                    onClick={() => {
                                                        toggleRating(message.id, 'dislike');
                                                    }}
                                                >
                                                    <ThumbsDownIcon />
                                                </MessageActionButton>
                                            </MessageActions>
                                        )}
                                </MessageScrollerItem>
                            ))}

                            {/* TEMPORARY (testing aid): raw tool activity, untranslated —
                                to be replaced by designed tool-activity states. */}
                            {activity && (
                                <Text size="xs" color="neutral-500">
                                    {activity}
                                </Text>
                            )}

                            {(replyFailed || replyUnclear) && (
                                <div className="flex flex-col items-start gap-cozy">
                                    {replyFailed && (
                                        <Text size="xs" color="red-600">
                                            {t('ask_plato__error')}
                                        </Text>
                                    )}
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        leftIcon={CircleHelpIcon}
                                        onClick={() => {
                                            sendEvent('ai_chat_support_requested', {
                                                trigger: supportTrigger,
                                                message_index: lastMessage?.messageIndex ?? 0,
                                            });
                                            // Pre-fills the composer with the question
                                            // that failed, so support opens on the
                                            // case rather than an empty box.
                                            showNewMessage(
                                                t('ask_plato__support_message', {
                                                    question: lastQuestion ?? '',
                                                }),
                                            );
                                        }}
                                    >
                                        {t('ask_plato__ask_support')}
                                    </Button>
                                </div>
                            )}
                        </MessageScrollerContent>
                    </MessageScrollerViewport>
                    <MessageScrollerButton />
                </MessageScroller>
            </MessageScrollerProvider>

            <SidePanelFooter>
                <PromptInput
                    onSubmit={(event) => {
                        event.preventDefault();
                        if (isStreaming || draft.trim().length === 0) {
                            return;
                        }
                        sendMessage(draft);
                        setDraft('');
                    }}
                >
                    <PromptInputTextarea
                        ref={composerRef}
                        rows={1}
                        name="prompt"
                        value={draft}
                        onChange={(event) => {
                            setDraft(event.target.value);
                        }}
                        placeholder={t('ask_plato__placeholder')}
                    />
                    <InputGroupAddon align="inline-end">
                        <PromptInputSubmit
                            intent="ai"
                            status={isStreaming ? 'streaming' : 'ready'}
                            disabled={isStreaming || draft.trim().length === 0}
                            label={t('ask_plato__send')}
                        />
                    </InputGroupAddon>
                </PromptInput>

                {/* Always visible rather than dismissible: the caveat applies to
                    every answer, not just the first one a user sees. */}
                <Text as="p" size="xs" color="neutral-500" className="mt-cozy text-balance text-center">
                    {t('ask_plato__disclaimer')}
                </Text>
            </SidePanelFooter>
        </SidePanelContent>
    );
}
